import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { clearBracketCache } from '../bracketCache';
import { collection, addDoc, getDocs, deleteDoc, query, where, doc, updateDoc, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const C = {
  bg:           '#0B1928',
  bgCard:       '#132030',
  border:       'rgba(255,255,255,0.09)',
  orange:       '#FF6B00',
  orangeDim:    'rgba(255,107,0,0.12)',
  orangeBorder: 'rgba(255,107,0,0.35)',
  textPrimary:  '#F0F4F8',
  textSecondary:'#8496A8',
  textMuted:    '#5A7A9A',
  green:        '#00C851',
  greenDim:     'rgba(0,200,81,0.10)',
  greenBorder:  'rgba(0,200,81,0.28)',
  red:          '#FF4444',
};
const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

const ADMIN_EMAILS = ['jelston314@gmail.com', 'danlandia200@gmail.com'];
const CATEGORIES   = ['Sports','Music','Movies','TV','Pop Culture','Gaming','Food','History'];
const SLOTS        = [1,32,16,17,8,25,9,24,4,29,13,20,5,28,12,21,2,31,15,18,7,26,10,23,3,30,14,19,6,27,11,22];

function buildEntries(ranked) {
  return SLOTS.map(seed => ranked[seed - 1]);
}

// ── ENTRIES HASH — used for repeat bracket detection (Path A) ─────────────────
function computeEntriesHash(entries) {
  const str = [...(entries||[])].sort().join('|');
  let h = 0;
  for (let i = 0; i < str.length; i++) { h = Math.imul(31, h) + str.charCodeAt(i) | 0; }
  return String(Math.abs(h));
}

// ── TIMEZONE-SAFE DATE HELPERS ────────────────────────────────────────────────
function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + n);
  return getLocalDateString(date);
}

function Admin({ user, onSignOut }) {
  const [adminTab,          setAdminTab]          = useState('brackets');
  const [date,              setDate]              = useState(getLocalDateString());
  const [category,          setCategory]          = useState('');
  const [keyword,           setKeyword]           = useState('Sports');
  const [description,       setDescription]       = useState('');
  const [playersText,       setPlayersText]       = useState('');
  const [saving,            setSaving]            = useState(false);
  const [isLocked,          setIsLocked]          = useState(false);
  const [insertPosition,    setInsertPosition]    = useState('');
  const [maxPosition,       setMaxPosition]       = useState(0);
  const [message,           setMessage]           = useState('');
  const [brackets,          setBrackets]          = useState([]);
  const [loadingBrackets,   setLoadingBrackets]   = useState(false);
  const [feedback,          setFeedback]          = useState([]);
  const [redating,          setRedating]          = useState(false);
  const [redateMessage,     setRedateMessage]     = useState('');
  const [redateStartDate,   setRedateStartDate]   = useState(getLocalDateString());
  const [showRedateConfirm, setShowRedateConfirm] = useState(false);
  const [scanning,          setScanning]          = useState(false);
  const [dashMetrics,       setDashMetrics]       = useState(null);
  const [dashLoading,       setDashLoading]       = useState(false);
  const [dashHistory,       setDashHistory]       = useState([]);
  const [scanResults,       setScanResults]       = useState(null);
  const [bufferStartDate,  setBufferStartDate]  = useState(getLocalDateString());
  const [bufferDays,       setBufferDays]        = useState(7);

  const [bufferPosts,      setBufferPosts]       = useState([]);
  const [bufferLoading,    setBufferLoading]     = useState(false);
  const [copiedIdx,        setCopiedIdx]         = useState(null);
  const [scanFilter,        setScanFilter]        = useState('all');

  useEffect(() => { loadBrackets(); loadFeedback(); loadMaxPosition(); }, []);
  useEffect(() => { if (adminTab === 'dashboard') loadDashboard(); }, [adminTab]); // eslint-disable-line

  const loadMaxPosition = async () => {
    try {
      const snap = await getDocs(collection(db,'brackets'));
      const positions = snap.docs.map(d => d.data().position).filter(p => typeof p === 'number');
      setMaxPosition(positions.length > 0 ? Math.max(...positions) : 0);
    } catch(err) { console.error(err); }
  };

  const loadFeedback = async () => {
    try {
      const snap  = await getDocs(collection(db,'feedback'));
      const items = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
      setFeedback(items);
    } catch (err) { console.error(err); }
  };

  const loadBrackets = async () => {
    setLoadingBrackets(true);
    try {
      const snap = await getDocs(collection(db,'brackets'));
      const all  = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        .sort((a,b) => a.date.localeCompare(b.date));
      setBrackets(all);
    } catch (err) { console.error(err); }
    setLoadingBrackets(false);
  };

  const handlePost = async () => {
    if (!category || !playersText.trim()) { setMessage('Please fill in all fields.'); return; }
    if (!isLocked && !insertPosition) { setMessage('Locked brackets need a date. General brackets need an insert position.'); return; }
    const ranked = playersText.split(',').map(p => p.trim()).filter(Boolean);
    if (ranked.length !== 32) { setMessage(`Need exactly 32 players — you have ${ranked.length}.`); return; }
    setSaving(true); setMessage('');
    try {
      const entries = buildEntries(ranked);
      const hash = computeEntriesHash(entries);

      if (isLocked) {
        // ── LOCKED BRACKET: date-based, replaces any existing on that date ──
        if (!date) { setMessage('Locked brackets need a date.'); setSaving(false); return; }
        const existing = await getDocs(query(collection(db,'brackets'), where('date','==',date), where('isLocked','==',true)));
        for (const d of existing.docs) await deleteDoc(doc(db,'brackets',d.id));
        const ref = await addDoc(collection(db,'brackets'), {
          date, category, keyword, description, isActive:true, isLocked:true,
          entries, entriesHash:hash,
        });
        setMessage(`✅ Locked bracket posted for ${date}! ID: ${ref.id}`);
      } else {
        // ── GENERAL BRACKET: insert at position, shift everything after it ──
        const insertAt = parseInt(insertPosition, 10);
        if (isNaN(insertAt) || insertAt < 1) { setMessage('Enter a valid position number.'); setSaving(false); return; }

        // Shift all existing general brackets at insertAt and above up by 1
        const snap = await getDocs(query(collection(db,'brackets'), where('isLocked','==',false)));
        const toShift = snap.docs
          .map(d => ({id:d.id, ...d.data()}))
          .filter(b => typeof b.position === 'number' && b.position >= insertAt)
          .sort((a,b) => b.position - a.position); // shift highest first to avoid conflicts

        const BATCH_SIZE = 400;
        let batch = [];
        for (const b of toShift) {
          batch.push(updateDoc(doc(db,'brackets',b.id), { position: b.position + 1 }));
          if (batch.length >= BATCH_SIZE) { await Promise.all(batch); batch = []; }
        }
        if (batch.length) await Promise.all(batch);

        // Insert new bracket at the target position
        const ref = await addDoc(collection(db,'brackets'), {
          category, keyword, description, isActive:true, isLocked:false,
          position: insertAt,
          entries, entriesHash:hash,
        });
        setMessage(`✅ Inserted at position ${insertAt}! ${toShift.length} brackets shifted. ID: ${ref.id}`);
        await loadMaxPosition();
      }

      setCategory(''); setDescription(''); setPlayersText(''); setInsertPosition('');
      loadBrackets();
    } catch (err) { console.error(err); setMessage('❌ Error posting bracket.'); }
    setSaving(false);
  };

  const handleDelete = async (bracketId, bracketDate) => {
    if (!window.confirm(`Delete bracket for ${bracketDate}?`)) return;
    try {
      await deleteDoc(doc(db,'brackets',bracketId));
      const ubSnap = await getDocs(query(collection(db,'userBrackets'), where('bracketId','==',bracketId)));
      for (const d of ubSnap.docs) await deleteDoc(doc(db,'userBrackets',d.id));
      clearBracketCache();
      setMessage(`Deleted ${bracketDate}`);
      loadBrackets();
    } catch (err) { setMessage('Error deleting.'); }
  };



  // ── BUFFER COPY GENERATOR ─────────────────────────────────────────────────
  const generateBufferPosts = async () => {
    setBufferLoading(true);
    setBufferPosts([]);
    try {
      const posts = [];
      for (let i = 0; i < bufferDays; i++) {
        const dateStr = addDays(bufferStartDate, i);
        const snap = await getDocs(query(collection(db,'brackets'), where('date','==',dateStr)));
        if (snap.empty) continue;
        const bracket = snap.docs[0].data();
        const cat = bracket.category || 'Today\'s Bracket';
        const kw  = bracket.keyword  || 'Sports';

        const emojis = { Sports:'🏆', Music:'🎵', Movies:'🎬', TV:'📺', Food:'🍕', Gaming:'🎮', History:'📜', 'Pop Culture':'🌟' };
        const emoji = emojis[kw] || '🏆';

        const allHooks = [
          `${emoji} ${cat} — who you got? Pick your champion and see how the world voted 👇\n\nplaybracketz.com\n\n#Bracketz #DailyBracket #${kw.replace(/ /g,'')}`,
          `Daily debate: ${cat} ${emoji}\n\nDrop your pick and see if you agree with the crowd.\n\nplaybracketz.com\n\n#Bracketz #${kw.replace(/ /g,'')} #DailyChallenge`,
          `${cat} 🔥 The bracket is live.\n\nPick your champion in under 2 minutes 👇\n\nplaybracketz.com\n\n#Bracketz #DailyBracket #${kw.replace(/ /g,'')}`,
          `Who wins ${cat}? ${emoji}\n\nThe daily bracket is up — make your picks and see how you compare.\n\nplaybracketz.com\n\n#Bracketz #${kw.replace(/ /g,'')}`,
        ];
        const hook = allHooks[i % allHooks.length];
        posts.push({ date: dateStr, category: cat, keyword: kw, post: hook });
      }
      setBufferPosts(posts);
    } catch(err) { console.error(err); }
    setBufferLoading(false);
  };

  const copyPost = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  // ── DASHBOARD ──────────────────────────────────────────────────────────────
  const loadDashboard = async () => {
    setDashLoading(true);
    try {
      // Load last 30 days of metrics snapshots
      const snap = await getDocs(query(collection(db,'metrics'), orderBy('date','desc'), limit(30)));
      const history = snap.docs.map(d => d.data());
      setDashHistory(history);
      if (history.length > 0) setDashMetrics(history[0]);
    } catch(err) { console.error(err); }
    setDashLoading(false);
  };

  const triggerMetricsNow = async () => {
    setDashLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'triggerMetricsNow');
      const result = await fn();
      if (result.data.success) {
        setDashMetrics(result.data.metrics);
        setDashHistory(prev => [result.data.metrics, ...prev.filter(m => m.date !== result.data.metrics.date)]);
        setMessage('✅ Metrics computed and saved!');
      }
    } catch(err) { console.error(err); setMessage('❌ Error computing metrics. Deploy the Cloud Function first.'); }
    setDashLoading(false);
  };

  // ── REDATE ALL BRACKETS ───────────────────────────────────────────────────
  const handleRedate = async () => {
    setRedating(true);
    setRedateMessage('');
    setShowRedateConfirm(false);
    try {
      const snap = await getDocs(collection(db,'brackets'));
      const all  = snap.docs
        .map(d => ({ id:d.id, ...d.data() }))
        .sort((a,b) => a.date.localeCompare(b.date));

      const BATCH_SIZE = 10;
      let updated = 0;

      for (let i = 0; i < all.length; i++) {
        const newDate = addDays(redateStartDate, i);
        await updateDoc(doc(db,'brackets', all[i].id), { date: newDate });
        updated++;
        if (updated % BATCH_SIZE === 0) {
          setRedateMessage(`Updated ${updated} of ${all.length}...`);
          await new Promise(r => setTimeout(r, 100));
        }
      }

      clearBracketCache();
      await loadBrackets();
      setRedateMessage(`✅ Done! ${updated} brackets redated starting from ${redateStartDate}.`);
    } catch (err) {
      console.error(err);
      setRedateMessage('❌ Error during redate. Some brackets may have been updated — reload and check.');
    }
    setRedating(false);
  };


  // ── DATA QUALITY SCANNER ─────────────────────────────────────────────────
  const handleScan = async () => {
    setScanning(true);
    setScanResults(null);
    try {
      const snap = await getDocs(collection(db,'brackets'));
      const all  = snap.docs.map(d => ({ id:d.id, ...d.data() }));
      const issues = [];
      const BAD_PATTERNS = [/^tbd$/i,/^tba$/i,/^placeholder$/i,/^unknown$/i,/^n\/a$/i,/^\?+$/,/^-+$/,/^entry \d+$/i,/^player \d+$/i,/^team \d+$/i];

      for (const b of all) {
        const bracketIssues = [];
        const entries = b.entries || [];

        // Wrong count
        if (entries.length !== 32) {
          bracketIssues.push({ type:'wrong_count', msg:`Has ${entries.length} entries (need 32)`, severity:'error' });
        }

        // Check each entry
        const seen = new Set();
        entries.forEach((entry, i) => {
          if (!entry || entry.trim() === '') {
            bracketIssues.push({ type:'empty', msg:`Entry ${i+1} is empty`, severity:'error' });
          } else if (entry.trim().length < 3) {
            bracketIssues.push({ type:'too_short', msg:`Entry ${i+1} "${entry}" is too short`, severity:'warning' });
          } else if (/^\d+$/.test(entry.trim())) {
            bracketIssues.push({ type:'number_only', msg:`Entry ${i+1} "${entry}" is just a number`, severity:'warning' });
          } else if (BAD_PATTERNS.some(p => p.test(entry.trim()))) {
            bracketIssues.push({ type:'placeholder', msg:`Entry ${i+1} "${entry}" looks like a placeholder`, severity:'error' });
          } else if (seen.has(entry.trim().toLowerCase())) {
            bracketIssues.push({ type:'duplicate', msg:`"${entry}" appears more than once`, severity:'error' });
          }
          seen.add(entry.trim().toLowerCase());
        });

        // Missing metadata
        if (!b.category || b.category.trim() === '') {
          bracketIssues.push({ type:'no_category', msg:'Missing bracket title', severity:'error' });
        }
        if (!b.keyword || b.keyword.trim() === '') {
          bracketIssues.push({ type:'no_keyword', msg:'Missing category/keyword', severity:'warning' });
        }

        if (bracketIssues.length > 0) {
          issues.push({ bracket:b, issues:bracketIssues });
        }
      }

      const errorCount   = issues.filter(i => i.issues.some(x => x.severity === 'error')).length;
      const warningCount = issues.filter(i => i.issues.every(x => x.severity === 'warning')).length;

      setScanResults({ total:all.length, issueCount:issues.length, errorCount, warningCount, issues });
    } catch (err) { console.error(err); }
    setScanning(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y,m,d] = dateStr.split('-');
    return new Date(y,m-1,d).toLocaleDateString('en-US',{ month:'short', day:'numeric', year:'numeric' });
  };

  const formatTs = (ts) => {
    if (!ts?.seconds) return '';
    return new Date(ts.seconds*1000).toLocaleDateString('en-US',{ month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  if (!user || !ADMIN_EMAILS.includes(user.email)) {
    return (
      <div style={{ padding:'40px', textAlign:'center', fontFamily:font }}>
        <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔒</div>
        <div style={{ fontSize:'20px', fontWeight:'700', color:C.textPrimary, marginBottom:'8px' }}>Access Denied</div>
        <div style={{ fontSize:'14px', color:C.textMuted }}>You don't have permission to view this page.</div>
      </div>
    );
  }

  const inputStyle = {
    width:'100%', backgroundColor:C.bgCard, border:`0.5px solid ${C.border}`,
    borderRadius:'8px', padding:'10px 12px', fontSize:'13px', color:C.textPrimary,
    fontFamily:font, outline:'none', boxSizing:'border-box',
  };

  return (
    <div style={{ maxWidth:'480px', margin:'0 auto', minHeight:'100vh', backgroundColor:C.bg, fontFamily:font }}>

      {/* Header */}
      <div style={{ backgroundColor:C.bg, padding:'14px 18px 12px', borderBottom:`0.5px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:99 }}>
        <div>
          <div style={{ fontSize:'16px', fontWeight:'700', color:C.orange, letterSpacing:'5px' }}>BRACKETZ</div>
          <div style={{ fontSize:'9px', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginTop:'2px' }}>Admin Panel</div>
        </div>
        <button onClick={onSignOut} style={{ backgroundColor:'transparent', border:`0.5px solid ${C.border}`, borderRadius:'8px', padding:'6px 12px', fontSize:'11px', color:C.textMuted, fontFamily:font, cursor:'pointer' }}>
          Sign Out
        </button>
      </div>

      <div style={{ padding:'16px', paddingBottom:'40px' }}>

        {/* Tab switcher */}
        <div style={{ display:'flex', backgroundColor:C.bgCard, borderRadius:'8px', padding:'3px', marginBottom:'16px', border:`0.5px solid ${C.border}` }}>
          {[
            { key:'brackets', label:'Brackets' },
            { key:'tools',    label:'Tools' },
            { key:'feedback', label:`Feedback (${feedback.length})` },
            { key:'dashboard', label:'Dashboard' },
          ].map(t => (
            <div key={t.key} onClick={() => setAdminTab(t.key)} style={{ flex:1, padding:'7px', textAlign:'center', fontSize:'11px', fontWeight:'700', borderRadius:'6px', cursor:'pointer', backgroundColor:adminTab===t.key?C.orange:'transparent', color:adminTab===t.key?'#FFF':C.textMuted, transition:'all 0.15s' }}>
              {t.label}
            </div>
          ))}
        </div>

        {/* ── BRACKETS TAB ── */}
        {adminTab === 'brackets' && (
          <div>
            <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`0.5px solid ${C.border}`, padding:'16px', marginBottom:'20px' }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.orange, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'16px' }}>Post New Bracket</div>

              {/* Locked toggle */}
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', padding:'10px 12px', background:'rgba(255,107,0,0.06)', borderRadius:'8px', border:`0.5px solid ${C.border}` }}>
                <div onClick={() => setIsLocked(l => !l)} style={{ width:'36px', height:'20px', borderRadius:'10px', background:isLocked?C.orange:'rgba(255,255,255,0.1)', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:'3px', left:isLocked?'19px':'3px', width:'14px', height:'14px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                </div>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:'600', color:C.textPrimary }}>{isLocked ? '🔒 Locked bracket (Holiday / Event)' : '📅 General bracket (position-based)'}</div>
                  <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'1px' }}>{isLocked ? 'Fixed to a specific date — never slides' : 'Inserted at a position — pushes later brackets forward'}</div>
                </div>
              </div>

              {/* Date (locked only) or Position (general only) */}
              {isLocked ? (
                <div style={{ marginBottom:'12px' }}>
                  <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Date</div>
                  <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inputStyle} />
                </div>
              ) : (
                <div style={{ marginBottom:'12px' }}>
                  <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Insert at position <span style={{color:C.textMuted,fontWeight:'400'}}>(current max: {maxPosition})</span></div>
                  <input type="number" min="1" max={maxPosition+1} value={insertPosition} onChange={e=>setInsertPosition(e.target.value)} placeholder={`e.g. ${maxPosition+1} (add to end)`} style={inputStyle} />
                  <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'4px' }}>Everything at this position and after will shift forward 1 day</div>
                </div>
              )}
              <div style={{ marginBottom:'12px' }}>
                <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Bracket Title</div>
                <input type="text" value={category} onChange={e=>setCategory(e.target.value)} placeholder="e.g. Greatest NBA Player of All Time" style={inputStyle} />
              </div>
              <div style={{ marginBottom:'12px' }}>
                <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Category</div>
                <select value={keyword} onChange={e=>setKeyword(e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                  {CATEGORIES.map(cat => <option key={cat} value={cat} style={{ backgroundColor:C.bgCard }}>{cat}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:'12px' }}>
                <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Description <span style={{ fontWeight:'400' }}>(optional)</span></div>
                <input type="text" value={description} onChange={e=>setDescription(e.target.value)} placeholder="Short description" style={inputStyle} />
              </div>
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Players — ranked 1 to 32, comma separated</div>
                <textarea value={playersText} onChange={e=>setPlayersText(e.target.value)} placeholder="Michael Jordan, LeBron James, ..." rows={5}
                  style={{ ...inputStyle, resize:'vertical', lineHeight:'1.5', fontSize:'12px' }} />
                <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'4px' }}>
                  {playersText.split(',').filter(p=>p.trim()).length} / 32 players
                </div>
              </div>

              {message && (
                <div style={{ fontSize:'12px', color:message.startsWith('✅')?C.green:C.red, marginBottom:'12px', padding:'10px 12px', backgroundColor:message.startsWith('✅')?C.greenDim:'rgba(255,68,68,0.1)', borderRadius:'8px', border:`0.5px solid ${message.startsWith('✅')?C.greenBorder:'rgba(255,68,68,0.3)'}` }}>
                  {message}
                </div>
              )}

              <button onClick={handlePost} disabled={saving} style={{ width:'100%', backgroundColor:saving?'rgba(255,107,0,0.5)':C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'14px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:saving?'not-allowed':'pointer' }}>
                {saving ? 'Posting...' : 'Post Bracket'}
              </button>
            </div>

            {/* Existing brackets */}
            <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary, marginBottom:'12px' }}>
              Existing Brackets ({brackets.length}) — sorted oldest first
            </div>
            {loadingBrackets ? (
              <div style={{ textAlign:'center', padding:'20px', color:C.textMuted }}>Loading...</div>
            ) : brackets.length === 0 ? (
              <div style={{ textAlign:'center', padding:'20px', color:C.textMuted, fontSize:'13px' }}>No brackets yet.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {brackets.map(bracket => (
                  <div key={bracket.id} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px', display:'flex', alignItems:'center', gap:'12px' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary, marginBottom:'3px' }}>{bracket.category}</div>
                      <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                        {bracket.isLocked
                          ? <span style={{ fontSize:'11px', color:C.textMuted }}>🔒 {formatDate(bracket.date)}</span>
                          : <span style={{ fontSize:'11px', color:C.textMuted }}>pos {bracket.position}</span>
                        }
                        {bracket.keyword && <span style={{ fontSize:'9px', fontWeight:'700', color:C.orange, backgroundColor:C.orangeDim, padding:'2px 7px', borderRadius:'6px' }}>{bracket.keyword}</span>}
                        {bracket.entriesHash && <span style={{ fontSize:'9px', color:'rgba(0,200,81,0.6)' }}>✓</span>}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(bracket.id, bracket.date)} style={{ backgroundColor:'rgba(255,68,68,0.1)', border:'0.5px solid rgba(255,68,68,0.3)', borderRadius:'8px', padding:'6px 12px', fontSize:'11px', color:C.red, fontFamily:font, cursor:'pointer', whiteSpace:'nowrap' }}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TOOLS TAB ── */}
        {adminTab === 'tools' && (
          <div>

            {/* ── BUFFER COPY GENERATOR ── */}
            <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`0.5px solid ${C.border}`, padding:'16px', marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.orange, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>📋 Buffer Copy Generator</div>
              <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.6', marginBottom:'16px' }}>
                Pick a start date and number of days. Get ready-to-copy posts for Buffer, X, Instagram, or TikTok.
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                <div>
                  <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Start Date</div>
                  <input type="date" value={bufferStartDate} onChange={e => setBufferStartDate(e.target.value)} style={inputStyle}/>
                </div>
                <div>
                  <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Days</div>
                  <select value={bufferDays} onChange={e => setBufferDays(Number(e.target.value))} style={{ ...inputStyle, cursor:'pointer' }}>
                    {[3,5,7,14,30].map(d => <option key={d} value={d} style={{ backgroundColor:C.bgCard }}>{d} days</option>)}
                  </select>
                </div>
              </div>

              <button onClick={generateBufferPosts} disabled={bufferLoading} style={{ width:'100%', backgroundColor:bufferLoading?'rgba(255,107,0,0.4)':C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'13px', fontSize:'13px', fontWeight:'700', fontFamily:font, cursor:bufferLoading?'not-allowed':'pointer', marginBottom:'16px' }}>
                {bufferLoading ? 'Generating...' : `Generate ${bufferDays} Days of Posts`}
              </button>

              {bufferPosts.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {bufferPosts.map((p, idx) => (
                    <div key={idx} style={{ backgroundColor:'rgba(0,0,0,0.2)', borderRadius:'10px', padding:'12px', border:`0.5px solid ${C.border}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                        <div>
                          <div style={{ fontSize:'10px', fontWeight:'700', color:C.orange }}>{p.date}</div>
                          <div style={{ fontSize:'10px', color:C.textMuted }}>{p.category}</div>
                        </div>
                        <button onClick={() => copyPost(p.post, idx)} style={{ backgroundColor:copiedIdx===idx?C.green:'rgba(255,107,0,0.15)', color:copiedIdx===idx?'#fff':C.orange, border:`0.5px solid ${copiedIdx===idx?C.green:C.orangeBorder}`, borderRadius:'6px', padding:'5px 12px', fontSize:'10px', fontWeight:'700', fontFamily:font, cursor:'pointer', transition:'all 0.15s' }}>
                          {copiedIdx===idx ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                      <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.6', whiteSpace:'pre-wrap' }}>{p.post}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── DATA QUALITY SCANNER ── */}
            <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`0.5px solid ${C.border}`, padding:'16px', marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.orange, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Data Quality Scanner</div>
              <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.6', marginBottom:'16px' }}>
                Scans all brackets for empty entries, duplicates, placeholders, wrong counts, and missing metadata. Run before beta launch.
              </div>

              <button onClick={handleScan} disabled={scanning} style={{ width:'100%', backgroundColor:scanning?'rgba(255,107,0,0.4)':C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'14px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:scanning?'not-allowed':'pointer', marginBottom:'16px' }}>
                {scanning ? 'Scanning...' : `🔍 Scan All ${brackets.length} Brackets`}
              </button>

              {scanResults && (
                <div>
                  {/* Summary */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
                    <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`0.5px solid ${C.border}`, padding:'12px', textAlign:'center' }}>
                      <div style={{ fontSize:'22px', fontWeight:'800', color:C.green }}>{scanResults.total - scanResults.issueCount}</div>
                      <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>Clean</div>
                    </div>
                    <div style={{ backgroundColor:'rgba(255,68,68,0.08)', borderRadius:'10px', border:'0.5px solid rgba(255,68,68,0.3)', padding:'12px', textAlign:'center' }}>
                      <div style={{ fontSize:'22px', fontWeight:'800', color:C.red }}>{scanResults.errorCount}</div>
                      <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>Errors</div>
                    </div>
                    <div style={{ backgroundColor:'rgba(245,158,11,0.08)', borderRadius:'10px', border:'0.5px solid rgba(245,158,11,0.3)', padding:'12px', textAlign:'center' }}>
                      <div style={{ fontSize:'22px', fontWeight:'800', color:'#F59E0B' }}>{scanResults.warningCount}</div>
                      <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>Warnings</div>
                    </div>
                  </div>

                  {scanResults.issueCount === 0 ? (
                    <div style={{ backgroundColor:C.greenDim, border:`0.5px solid ${C.greenBorder}`, borderRadius:'10px', padding:'16px', textAlign:'center' }}>
                      <div style={{ fontSize:'24px', marginBottom:'6px' }}>✅</div>
                      <div style={{ fontSize:'14px', fontWeight:'700', color:C.green }}>All {scanResults.total} brackets are clean!</div>
                      <div style={{ fontSize:'12px', color:C.textMuted, marginTop:'4px' }}>No issues found. Ready for launch.</div>
                    </div>
                  ) : (
                    <div>
                      {/* Filter */}
                      <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
                        {['all','errors','warnings'].map(f => (
                          <div key={f} onClick={() => setScanFilter(f)} style={{ fontSize:'10px', fontWeight:'700', padding:'4px 12px', borderRadius:'20px', cursor:'pointer', backgroundColor:scanFilter===f?C.orange:'transparent', color:scanFilter===f?'#FFF':C.textMuted, border:`0.5px solid ${scanFilter===f?C.orange:'rgba(255,255,255,0.1)'}` }}>
                            {f === 'all' ? `All (${scanResults.issueCount})` : f === 'errors' ? `Errors (${scanResults.errorCount})` : `Warnings (${scanResults.warningCount})`}
                          </div>
                        ))}
                      </div>

                      {/* Issue list */}
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px', maxHeight:'400px', overflowY:'auto' }}>
                        {scanResults.issues
                          .filter(item => {
                            if (scanFilter === 'errors')   return item.issues.some(x => x.severity === 'error');
                            if (scanFilter === 'warnings') return item.issues.every(x => x.severity === 'warning');
                            return true;
                          })
                          .map((item, idx) => (
                            <div key={idx} style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`0.5px solid ${item.issues.some(x=>x.severity==='error')?'rgba(255,68,68,0.35)':'rgba(245,158,11,0.35)'}`, padding:'12px' }}>
                              <div style={{ fontSize:'12px', fontWeight:'700', color:C.textPrimary, marginBottom:'2px' }}>{item.bracket.category || 'Untitled'}</div>
                              <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'8px' }}>{item.bracket.date || 'No date'} · {item.bracket.keyword || 'No keyword'}</div>
                              {item.issues.map((issue, i) => (
                                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'6px', marginBottom:'4px' }}>
                                  <span style={{ fontSize:'10px', flexShrink:0 }}>{issue.severity === 'error' ? '🔴' : '🟡'}</span>
                                  <span style={{ fontSize:'11px', color:issue.severity === 'error' ? '#FCA5A5' : '#FCD34D', lineHeight:'1.4' }}>{issue.msg}</span>
                                </div>
                              ))}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`0.5px solid ${C.border}`, padding:'16px', marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.orange, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Redate All Brackets</div>
              <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.6', marginBottom:'16px' }}>
                Keeps all brackets in their current order and reassigns consecutive daily dates starting from the date you choose.
              </div>

              <div style={{ marginBottom:'12px' }}>
                <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'6px', fontWeight:'600' }}>Start date — this becomes bracket #1</div>
                <input type="date" value={redateStartDate} onChange={e => setRedateStartDate(e.target.value)} style={inputStyle} />
              </div>

              <div style={{ backgroundColor:'rgba(255,68,68,0.06)', borderRadius:'10px', padding:'12px', marginBottom:'16px', border:`0.5px solid rgba(255,68,68,0.2)` }}>
                <div style={{ fontSize:'11px', color:'rgba(255,100,100,0.9)', fontWeight:'700', marginBottom:'4px' }}>⚠️ This updates all {brackets.length} brackets</div>
                <div style={{ fontSize:'11px', color:C.textMuted, lineHeight:'1.5' }}>
                  Bracket content is unchanged. Only dates are updated. This cannot be undone.
                </div>
              </div>

              {redateMessage && (
                <div style={{ fontSize:'12px', color:redateMessage.startsWith('✅')?C.green:redateMessage.startsWith('❌')?C.red:C.textSecondary, marginBottom:'12px', padding:'10px 12px', backgroundColor:redateMessage.startsWith('✅')?C.greenDim:redateMessage.startsWith('❌')?'rgba(255,68,68,0.1)':C.bgCard, borderRadius:'8px', border:`0.5px solid ${redateMessage.startsWith('✅')?C.greenBorder:redateMessage.startsWith('❌')?'rgba(255,68,68,0.3)':C.border}` }}>
                  {redateMessage}
                </div>
              )}

              {!showRedateConfirm ? (
                <button onClick={() => setShowRedateConfirm(true)} disabled={redating} style={{ width:'100%', backgroundColor:'rgba(255,68,68,0.15)', color:C.red, border:'0.5px solid rgba(255,68,68,0.4)', borderRadius:'10px', padding:'14px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:redating?'not-allowed':'pointer' }}>
                  Redate {brackets.length} Brackets
                </button>
              ) : (
                <div>
                  <div style={{ fontSize:'13px', color:C.textPrimary, fontWeight:'700', textAlign:'center', marginBottom:'12px' }}>
                    Are you sure? This will redate all {brackets.length} brackets starting {redateStartDate}.
                  </div>
                  <div style={{ display:'flex', gap:'10px' }}>
                    <button onClick={handleRedate} disabled={redating} style={{ flex:1, backgroundColor:redating?'rgba(255,68,68,0.3)':'rgba(255,68,68,0.8)', color:'#fff', border:'none', borderRadius:'10px', padding:'14px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:redating?'not-allowed':'pointer' }}>
                      {redating ? redateMessage || 'Redating...' : 'Yes, Redate All'}
                    </button>
                    <button onClick={() => setShowRedateConfirm(false)} disabled={redating} style={{ flex:1, backgroundColor:'transparent', color:C.textMuted, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px', fontSize:'14px', cursor:'pointer', fontFamily:font }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {/* ── DASHBOARD TAB ── */}
        {adminTab === 'dashboard' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>
                {dashMetrics ? `Stats for ${dashMetrics.date}` : 'No metrics yet'}
              </div>
              <button onClick={triggerMetricsNow} disabled={dashLoading} style={{ backgroundColor:dashLoading?'rgba(255,107,0,0.4)':C.orange, color:'#fff', border:'none', borderRadius:'8px', padding:'8px 14px', fontSize:'11px', fontWeight:'700', fontFamily:font, cursor:dashLoading?'not-allowed':'pointer' }}>
                {dashLoading ? 'Computing...' : '⚡ Compute Now'}
              </button>
            </div>

            {message && (
              <div style={{ fontSize:'12px', color:message.startsWith('✅')?C.green:C.red, marginBottom:'12px', padding:'10px 12px', backgroundColor:message.startsWith('✅')?C.greenDim:'rgba(255,68,68,0.1)', borderRadius:'8px', border:`0.5px solid ${message.startsWith('✅')?C.greenBorder:'rgba(255,68,68,0.3)'}` }}>
                {message}
              </div>
            )}

            {dashLoading && !dashMetrics && (
              <div style={{ textAlign:'center', padding:'40px', color:C.textMuted }}>Loading metrics...</div>
            )}

            {dashMetrics && (
              <>
                {/* Core stats grid */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
                  {[
                    { label:'DAU', value:dashMetrics.dau, color:C.orange },
                    { label:'MAU', value:dashMetrics.mau, color:C.textPrimary },
                    { label:'DAU/MAU', value:dashMetrics.dauMauRatio+'%', color:dashMetrics.dauMauRatio>=40?C.green:dashMetrics.dauMauRatio>=20?'#F59E0B':'#EF4444' },
                    { label:'New Users', value:dashMetrics.newUsersToday, color:C.green },
                    { label:'Total Users', value:dashMetrics.totalUsers, color:C.textPrimary },
                    { label:'Pro Subscribers', value:dashMetrics.proSubscribers, color:'#FFD700' },
                    { label:'Submissions Today', value:dashMetrics.submissionsToday, color:C.orange },
                    { label:'Avg Streak', value:dashMetrics.avgStreak+' days', color:'#F59E0B' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`0.5px solid ${C.border}`, padding:'12px', textAlign:'center' }}>
                      <div style={{ fontSize:'22px', fontWeight:'800', color }}>{value}</div>
                      <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'3px' }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Retention */}
                <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px', marginBottom:'12px' }}>
                  <div style={{ fontSize:'9px', color:C.orange, fontWeight:'700', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'10px' }}>Retention</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                    {[
                      { label:'D1', val:dashMetrics.retention?.d1, good:40, ok:20 },
                      { label:'D7', val:dashMetrics.retention?.d7, good:20, ok:10 },
                      { label:'D30', val:dashMetrics.retention?.d30, good:10, ok:5 },
                    ].map(({ label, val, good, ok }) => (
                      <div key={label} style={{ textAlign:'center', padding:'10px', backgroundColor:'rgba(0,0,0,0.2)', borderRadius:'8px' }}>
                        <div style={{ fontSize:'20px', fontWeight:'800', color: val===null?C.textMuted:val>=good?C.green:val>=ok?'#F59E0B':'#EF4444' }}>
                          {val !== null ? val+'%' : 'n/a'}
                        </div>
                        <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>{label} Retention</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Streak distribution */}
                <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px', marginBottom:'12px' }}>
                  <div style={{ fontSize:'9px', color:C.orange, fontWeight:'700', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'10px' }}>Streak Distribution</div>
                  {dashMetrics.streakDistribution && Object.entries(dashMetrics.streakDistribution).map(([bucket, count]) => {
                    const max = Math.max(...Object.values(dashMetrics.streakDistribution));
                    const pct = max > 0 ? Math.round((count/max)*100) : 0;
                    return (
                      <div key={bucket} style={{ marginBottom:'6px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
                          <span style={{ fontSize:'11px', color:C.textSecondary }}>{bucket} days</span>
                          <span style={{ fontSize:'11px', fontWeight:'700', color:C.orange }}>{count}</span>
                        </div>
                        <div style={{ height:'4px', backgroundColor:'rgba(255,255,255,0.06)', borderRadius:'2px', overflow:'hidden' }}>
                          <div style={{ width:pct+'%', height:'100%', backgroundColor:C.orange, borderRadius:'2px' }}/>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop:'8px', fontSize:'11px', color:'#FFD700', fontWeight:'700' }}>
                    🏆 Longest streak: {dashMetrics.maxStreak} days
                  </div>
                </div>

                {/* Top brackets */}
                {dashMetrics.topBracketsThisWeek?.length > 0 && (
                  <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px', marginBottom:'12px' }}>
                    <div style={{ fontSize:'9px', color:C.orange, fontWeight:'700', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'10px' }}>Top Brackets This Week</div>
                    {dashMetrics.topBracketsThisWeek.map((b, i) => (
                      <div key={b.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:`0.5px solid ${C.border}` }}>
                        <span style={{ fontSize:'12px', color:C.textSecondary }}>{i+1}. {b.category}</span>
                        <span style={{ fontSize:'12px', fontWeight:'700', color:C.orange }}>{b.count} picks</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* 30-day DAU trend */}
                {dashHistory.length > 1 && (
                  <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px', marginBottom:'12px' }}>
                    <div style={{ fontSize:'9px', color:C.orange, fontWeight:'700', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'10px' }}>DAU Trend (last {dashHistory.length} days)</div>
                    <div style={{ display:'flex', alignItems:'flex-end', gap:'3px', height:'60px' }}>
                      {[...dashHistory].reverse().map((m, i) => {
                        const maxDau = Math.max(...dashHistory.map(d => d.dau || 0));
                        const h = maxDau > 0 ? Math.round(((m.dau||0)/maxDau)*100) : 0;
                        return (
                          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
                            <div style={{ width:'100%', height:h+'%', backgroundColor:i===dashHistory.length-1?C.orange:'rgba(255,107,0,0.3)', borderRadius:'2px 2px 0 0', minHeight:'2px' }}/>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}>
                      <span style={{ fontSize:'9px', color:C.textMuted }}>{dashHistory.length > 0 ? [...dashHistory].reverse()[0]?.date : ''}</span>
                      <span style={{ fontSize:'9px', color:C.textMuted }}>Today</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {!dashMetrics && !dashLoading && (
              <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'24px', textAlign:'center' }}>
                <div style={{ fontSize:'32px', marginBottom:'10px' }}>📊</div>
                <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'6px' }}>No metrics yet</div>
                <div style={{ fontSize:'12px', color:C.textMuted, marginBottom:'16px', lineHeight:'1.5' }}>
                  Deploy the Cloud Function, then click "Compute Now" to generate your first snapshot.
                </div>
                <button onClick={triggerMetricsNow} style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'10px', padding:'12px 20px', fontSize:'13px', fontWeight:'700', fontFamily:font, cursor:'pointer' }}>
                  ⚡ Compute First Snapshot
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── FEEDBACK TAB ── */}
        {adminTab === 'feedback' && (
          <div>
            {feedback.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px', color:C.textMuted, fontSize:'13px' }}>
                <div style={{ fontSize:'32px', marginBottom:'12px' }}>💬</div>
                No feedback yet.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {feedback.map(f => (
                  <div key={f.id} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${C.border}`, padding:'14px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                      <div style={{ backgroundColor:C.orangeDim, border:`0.5px solid ${C.orangeBorder}`, borderRadius:'6px', padding:'2px 8px', fontSize:'10px', color:C.orange, fontWeight:'700' }}>{f.type}</div>
                      <div style={{ fontSize:'10px', color:C.textMuted }}>{f.email}</div>
                    </div>
                    <div style={{ fontSize:'13px', color:C.textSecondary, lineHeight:'1.5', marginBottom:'8px' }}>{f.message}</div>
                    <div style={{ fontSize:'10px', color:C.textMuted }}>{formatTs(f.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default Admin;
